'use client'

import { memo, useEffect, useRef, type CSSProperties } from 'react'
import { useNinxl8BitThemeMode } from '@/app/(marketing)/_components/8bit'
import { NIN_SCROLL_WORLD_CONFIG } from './nin-scroll-world-config'
import { mountScrollWorld } from './scrub-engine'
import { ScrollWorldExplainerPlay } from './scroll-world-explainer-play'

const SCROLL_WORLD_THEME: Record<
  'light' | 'dark',
  Record<string, string>
> = {
  light: {
    '--sw-bg': '#f6f9fd',
    '--sw-ink': '#1a2a32',
    '--sw-ink-soft': '#88a1ac',
    '--sw-accent': '#21728d',
    '--sw-font-display': 'var(--font-geist-mono, ui-monospace, monospace)',
    '--sw-font-body': 'var(--font-geist-sans, system-ui, sans-serif)',
  },
  dark: {
    '--sw-bg': '#0c1218',
    '--sw-ink': '#e8f2f6',
    '--sw-ink-soft': '#8aa0ad',
    '--sw-accent': '#3d9db8',
    '--sw-font-display': 'var(--font-geist-mono, ui-monospace, monospace)',
    '--sw-font-body': 'var(--font-geist-sans, system-ui, sans-serif)',
  },
}

/** Mounts the portable scroll-scrub engine for the NIN org hero. */
export const NinScrollWorld = memo(function NinScrollWorld() {
  const rootRef = useRef<HTMLDivElement>(null)
  const theme = useNinxl8BitThemeMode()
  const themeVars = SCROLL_WORLD_THEME[theme === 'dark' ? 'dark' : 'light']

  useEffect(() => {
    const el = rootRef.current
    if (!el) return

    mountScrollWorld(el, NIN_SCROLL_WORLD_CONFIG)

    // baby: Fixed sky/stage only while the scrub track is in view.
    // hide above the craft hero AND after the track ends.
    const onScroll = () => {
      const track = el.querySelector<HTMLElement>('.sw-track')
      if (!track) {
        el.classList.add('nin-scroll-world--past')
        document.documentElement.classList.remove('nin-sw-active')
        return
      }
      const rect = track.getBoundingClientRect()
      const trackTop = window.scrollY + rect.top
      const trackEnd = trackTop + rect.height
      const viewTop = window.scrollY
      const viewBottom = viewTop + window.innerHeight
      const entered = viewBottom >= trackTop + 48
      const past = viewTop >= trackEnd - window.innerHeight * 0.35
      const active = entered && !past
      el.classList.toggle('nin-scroll-world--past', !active)
      document.documentElement.classList.toggle('nin-sw-active', active)
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    onScroll()

    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
      document.documentElement.classList.remove('nin-sw-active')
      el.replaceChildren()
      el.classList.remove('sw-root', 'nin-scroll-world--past')
    }
  }, [])

  return (
    <section
      data-section="hero"
      aria-label="Fly through the NIN sanctuary"
      className="nin-scroll-world relative z-[5]"
    >
      <div
        ref={rootRef}
        className="sw-root nin-scroll-world__root nin-scroll-world--past"
        style={themeVars as CSSProperties}
      />
      <ScrollWorldExplainerPlay
        ariaLabel="Watch NIN trailer"
        dialogTitle="NIN trailer"
      />
    </section>
  )
})